import React, { useEffect, useState } from "react";
import { Button, Input, Label, Table } from "reactstrap";
import { toast } from "react-toastify";
import ChecklistOutlinedIcon from "@mui/icons-material/ChecklistOutlined";
import {
  listCompanyVehicleCheckScheduleApi,
  createCompanyVehicleCheckScheduleApi,
  updateCompanyVehicleCheckScheduleApi,
  deleteCompanyVehicleCheckScheduleApi,
} from "../../../../helpers/fakebackend_helper";
import AppModal from "../../../../Components/Common/AppModal";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import CheckNotificationFields, {
  CHECK_SCHEDULE_KEYS,
  scheduleSummary,
} from "../../../../Components/Common/CheckNotificationFields";
import {
  RepeatIntervalInput,
  repeatLabel,
} from "../../../../Components/Common/RepeatInterval";

// Change #100679 - Paul: "i want to create 7 days check as company vehicle
// check schedule then assign it to the vehicle. that way say i wanted to
// change the due date from 0 days to 1 day i can do it in global and it
// applies to all vehicles rather than me changing vehicle by vehicle".
//
// Named schedules (repeat + Notifications). A check picks one on its Add/Edit
// popup; saving a schedule here updates every open check that uses it.
const BLANK = { Name: "", FlagActive: 1 };

export default function CheckSchedulesPanel() {
  const [list, setList] = useState([]);
  // null = loading, 0 = the table is not there yet (delta pending), 1 = ready.
  const [ready, setReady] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = () =>
    listCompanyVehicleCheckScheduleApi().then((res) => {
      if (res?.status === 200) {
        setList(res.companyvehiclecheckschedule_list || []);
        setReady(Number(res.ready) ? 1 : 0);
      } else {
        setReady(0);
        toast.error(res?.message || "Could not load the check schedules");
      }
    });

  useEffect(() => {
    load();
  }, []);

  const handleSave = () => {
    if (!String(editing?.Name || "").trim()) {
      toast.error("Please give the schedule a name");
      return;
    }
    const row = {
      Name: editing.Name,
      FlagActive: Number(editing.FlagActive) ? 1 : 0,
    };
    CHECK_SCHEDULE_KEYS.forEach((key) => {
      row[key] = editing[key] ?? "";
    });
    if (!(Number(row.RepeatIntervalDays) > 0)) {
      row.RepeatIntervalDays = "";
      row.RepeatIntervalUnit = "";
    }
    setSaving(true);
    const call = editing.Id
      ? updateCompanyVehicleCheckScheduleApi([{ ...row, Id: editing.Id }])
      : createCompanyVehicleCheckScheduleApi([row]);
    call.then((res) => {
      setSaving(false);
      if (res?.status === 200) {
        toast.success(res.message || "Saved");
        setEditing(null);
        load();
      } else {
        toast.error(res?.message || "Could not save the schedule");
      }
    });
  };

  const handleDelete = () => {
    deleteCompanyVehicleCheckScheduleApi([deleteId]).then((res) => {
      if (res?.status === 200) {
        toast.success(res.message || "Deleted");
        load();
      } else {
        toast.error(res?.message || "Could not delete the schedule");
      }
      setDeleteId(null);
    });
  };

  if (ready === null) {
    return <div className="text-muted p-3">Loading...</div>;
  }
  if (!ready) {
    return (
      <div className="text-muted p-3">
        Check schedules will be available once the pending database update has
        been run for this company.
      </div>
    );
  }

  return (
    <div className="p-2">
      <DeleteModal
        show={!!deleteId}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteId(null)}
      />
      <div className="d-flex justify-content-between align-items-start gap-2 mb-2 flex-wrap">
        <p className="text-muted mb-0" style={{ maxWidth: 640 }}>
          Set up a schedule once (for example "7 day van check") and pick it on
          each vehicle's check. Editing a schedule updates every open check that
          uses it.
        </p>
        <Button
          color="primary"
          size="sm"
          onClick={() => setEditing({ ...BLANK })}
        >
          <i className="ri-add-line me-1" />
          Add schedule
        </Button>
      </div>

      {list.length === 0 ? (
        <div className="text-muted p-3">No check schedules yet.</div>
      ) : (
        <div className="table-responsive">
          <Table className="align-middle mb-0">
            <thead>
              <tr>
                <th>Name</th>
                <th>Repeat</th>
                <th>Notifications</th>
                <th>Open checks</th>
                <th>Active</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.map((s) => (
                <tr key={s.Id}>
                  <td className="fw-semibold">{s.Name}</td>
                  <td>
                    {Number(s.RepeatIntervalDays) > 0
                      ? repeatLabel(s.RepeatIntervalDays, s.RepeatIntervalUnit)
                      : "One-off"}
                  </td>
                  <td>
                    <small className="text-muted">{scheduleSummary(s)}</small>
                  </td>
                  <td>{s.QtyOpenChecks || 0}</td>
                  <td>{Number(s.FlagActive) ? "Yes" : "No"}</td>
                  <td className="text-end text-nowrap">
                    <Button
                      color="light"
                      size="sm"
                      className="me-1"
                      onClick={() => setEditing({ ...s })}
                    >
                      Edit
                    </Button>
                    <Button
                      color="soft-danger"
                      size="sm"
                      onClick={() => setDeleteId(s.Id)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {editing && (
        <AppModal
          open={!!editing}
          onClose={() => setEditing(null)}
          title={editing.Id ? "Edit Check Schedule" : "Add Check Schedule"}
          icon={<ChecklistOutlinedIcon />}
          headerAccent="indigo"
          size="sm"
          loading={saving}
          loadingText="Saving…"
          onSubmit={handleSave}
          submitLabel={editing.Id ? "Update" : "Create"}
          submitColor="success"
        >
          <Label>Name</Label>
          <Input
            className="form-control"
            placeholder="e.g. 7 day van check"
            value={editing.Name || ""}
            onChange={(e) =>
              setEditing((prev) => ({ ...prev, Name: e.target.value }))
            }
          />
          <div className="form-check form-switch mt-2">
            <Input
              className="form-check-input"
              type="checkbox"
              id="checkScheduleActive"
              checked={!!Number(editing.FlagActive)}
              onChange={(e) =>
                setEditing((prev) => ({
                  ...prev,
                  FlagActive: e.target.checked ? 1 : 0,
                }))
              }
            />
            <Label className="form-check-label" for="checkScheduleActive">
              Active (offered on new checks)
            </Label>
          </div>
          <Label className="mt-2">Repeat every</Label>
          <RepeatIntervalInput
            days={editing.RepeatIntervalDays ?? ""}
            unit={editing.RepeatIntervalUnit}
            onChange={(patch) => setEditing((prev) => ({ ...prev, ...patch }))}
          />
          <small className="text-muted d-block mb-3">
            {Number(editing.RepeatIntervalDays) > 0
              ? `${repeatLabel(
                  editing.RepeatIntervalDays,
                  editing.RepeatIntervalUnit
                )}. Completing a check creates the next one.`
              : "Blank = a one-off check."}
          </small>
          <CheckNotificationFields
            idPrefix="checkSchedule"
            value={editing}
            onChange={(patch) => setEditing((prev) => ({ ...prev, ...patch }))}
          />
          {editing.Id && Number(editing.QtyOpenChecks) > 0 && (
            <small className="text-warning d-block mt-3">
              Saving updates the {editing.QtyOpenChecks} open check(s) on this
              schedule.
            </small>
          )}
        </AppModal>
      )}
    </div>
  );
}
